import { useState, useEffect, type ReactNode } from 'react';
import { Box, RefreshCw, Trash2, Download, ExternalLink, Search, X, Check, Loader2, Clock, Layers, Settings2 } from 'lucide-react';
import { FormModal } from '../../components/ui/FormModal';
import { Toast } from '../../components/ui/FormField';
import { Field, TextInput, SelectInput, PrimaryButton, GhostButton } from '../../components/ui/Field';
import { SortHeader, makeSortToggle } from '../../components/ui/SortHeader';
import { Cell, PaginationControls } from '../../components/ui/grid';
import { usePageHeader } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { companyApi, siteApi, wellApi, cloudProcessApi } from '../../services/api-services';
import { useAuthStore } from '../../stores/auth-store';
import type { Company, Site, CloudProcessItem, GeonomicFileItem } from '../../types/api';

export function ThreeDModelPage() {
  const authUser = useAuthStore((s) => s.user);
  const isSuperAdmin = authUser?.role === 'SuperAdmin';
  const recordActivity = useActivityStore((s) => s.record);

  // Dropdowns
  const [companies, setCompanies] = useState<Company[]>([]);
  const [ownCompanyName, setOwnCompanyName] = useState('');
  const [sites, setSites] = useState<Site[]>([]);
  const [wells, setWells] = useState<{ id: number; wellName: string }[]>([]);
  const [lasFiles, setLasFiles] = useState<GeonomicFileItem[]>([]);

  // Selection state
  const [companyId, setCompanyId] = useState(0);
  const [siteId, setSiteId] = useState(0);
  const [wellId, setWellId] = useState(0);
  const [lasFileId, setLasFileId] = useState(0);

  // Process form
  const [fromLength, setFromLength] = useState('');
  const [toLength, setToLength] = useState('');
  const [description, setDescription] = useState('');
  const [processing, setProcessing] = useState(false);

  // Grid
  const [data, setData] = useState<CloudProcessItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [sortCol, setSortCol] = useState('Id');
  const [sortDir, setSortDir] = useState<'Ascending' | 'Descending'>('Descending');
  const [searchText, setSearchText] = useState('');
  const pageSize = 100;

  // Download modal
  const [downloadModal, setDownloadModal] = useState(false);
  const [selectedCloud, setSelectedCloud] = useState<CloudProcessItem | null>(null);
  const [downloading, setDownloading] = useState(false);

  // Toast — auto-dismiss is owned by the Toast component (4s success / 6s error).
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' = 'success') => setToast({ message, type });

  usePageHeader('3D Models', null, []);

  // Load companies (SuperAdmin gets the dropdown list; other roles fetch their own company name)
  useEffect(() => {
    if (isSuperAdmin) {
      companyApi.getAllActive({ limit: 1000 }).then((r) => setCompanies(r.entities ?? [])).catch(() => {});
    } else {
      companyApi.getById(Number(authUser?.companyId ?? 0)).then((r) => setOwnCompanyName(r.entity?.companyName ?? '')).catch(() => {});
    }
  }, [isSuperAdmin, authUser?.companyId]);

  // Load sites when company changes (SuperAdmin picks a company; other roles are scoped to their own)
  useEffect(() => {
    const effectiveCompanyId = isSuperAdmin ? companyId : Number(authUser?.companyId ?? 0);
    if (effectiveCompanyId > 0) {
      siteApi.getAllByCompanyId(effectiveCompanyId, { limit: 1000 }).then((r) => setSites(r.entities ?? [])).catch(() => {});
    } else {
      setSites([]);
    }
    setSiteId(0);
    setOffset(0);
  }, [isSuperAdmin, companyId, authUser?.companyId]);

  // Load wells when site changes
  useEffect(() => {
    if (siteId > 0) {
      wellApi.getAllBySiteId(siteId, { limit: 1000 }).then((r) => setWells(r.entities ?? [])).catch(() => {});
    } else {
      setWells([]);
    }
    setWellId(0);
    setLasFiles([]);
    setLasFileId(0);
  }, [siteId]);

  // Load LAS files when well changes
  useEffect(() => {
    if (wellId > 0) {
      cloudProcessApi.getGeonomicFiles(wellId).then((r) => setLasFiles(r.entities ?? [])).catch(() => setLasFiles([]));
    } else {
      setLasFiles([]);
    }
    setLasFileId(0);
  }, [wellId]);

  // Load grid when site changes
  const loadGrid = () => {
    if (siteId <= 0) {
      setData([]);
      setTotalCount(0);
      return;
    }
    setLoading(true);
    cloudProcessApi.getAllBySiteId(siteId, { offset, limit: pageSize, sortCol, sortDirection: sortDir })
      .then((r) => {
        setData((r.entities ?? []).filter(e => !e.isDeleted));
        setTotalCount(r.numberofEntities ?? 0);
      })
      .catch(() => showToast('Failed to load models', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadGrid(); }, [siteId, offset, sortCol, sortDir]);

  // Process Model
  const handleProcess = async () => {
    if (!wellId || !lasFileId) {
      showToast('Please select Well and LAS File', 'error');
      return;
    }
    if (!fromLength || !toLength) {
      showToast('Please enter From Length and To Length', 'error');
      return;
    }
    if (Number(toLength) < Number(fromLength)) {
      showToast('From Length should be less than To Length', 'error');
      return;
    }

    // Find the selected LAS file to get its reference_DrillSurveyHead_Id (V2 uses this as DrillSurvayId)
    const selectedLas = lasFiles.find(f => f.id === lasFileId);
    const drillSurvayId = selectedLas?.reference_DrillSurveyHead_Id ?? 0;

    setProcessing(true);
    showToast('Process started...', 'success');
    try {
      await cloudProcessApi.process({
        wellGeonomicId: lasFileId,
        drillSurvayId,
        wellId,
        fromLength: Number(fromLength),
        toLength: Number(toLength),
        description,
        cloudHeadId: 0,
        perfectCloudStatus: 0,
        actualCloudStatus: 0,
        plyFileStatus: 0,
        id: 0,
      });
      showToast('Process completed. Check the status in the table.');
      const wellName = wells.find(w => w.id === wellId)?.wellName || 'Well';
      recordActivity({ type: 'create', entity: '3D Models', label: description?.trim() || wellName, user: authUser?.username });
      loadGrid();
    } catch {
      showToast('Failed to process model', 'error');
    } finally {
      setProcessing(false);
    }
  };

  // Delete
  const handleDelete = async (row: CloudProcessItem) => {
    if (!confirm('Do you want to delete the 3D Model?')) return;
    try {
      await cloudProcessApi.delete(row.id);
      showToast('Cloud Process deleted successfully');
      recordActivity({ type: 'delete', entity: '3D Models', label: row.description?.trim() || row.wellName || `Model #${row.id}`, user: authUser?.username });
      loadGrid();
    } catch {
      showToast('Failed to delete', 'error');
    }
  };

  // Open download modal
  const openDownloadModal = async (row: CloudProcessItem) => {
    try {
      const r = await cloudProcessApi.getById(row.id);
      const cloud = r.entity ?? row;
      // Try to get well name from our wells list
      const well = wells.find(w => w.id === (cloud.wellId ?? row.wellId));
      if (well) cloud.wellName = well.wellName;
      setSelectedCloud(cloud);
      setDownloadModal(true);
    } catch {
      setSelectedCloud(row);
      setDownloadModal(true);
    }
  };

  // Download PLY
  const handleDownload = async (cloudType: 'A' | 'P') => {
    if (!selectedCloud) return;
    const suffix = cloudType === 'A' ? 'Actual' : 'Perfect';
    const fileName = `${selectedCloud.wellName ?? 'Well'}_${selectedCloud.fromLength}m-${selectedCloud.toLength}m_${suffix}_${selectedCloud.id}.ply`;
    setDownloading(true);
    try {
      const blob = await cloudProcessApi.downloadPlyFile(selectedCloud.id, cloudType);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast(`Failed to download ${fileName}`, 'error');
    } finally {
      setDownloading(false);
    }
  };

  // Open 3D viewer
  const open3DViewer = (row: CloudProcessItem) => {
    const token = localStorage.getItem('geo_access_token') || '';
    const viewerBase = import.meta.env.VITE_3D_VIEWER_URL || 'https://gviewtest.geonomictech.com';
    window.open(`${viewerBase}/?fileId=${row.id}&wellId=${row.wellId}&token=${token}`, '_blank');
  };

  const handleSort = makeSortToggle(sortCol, sortDir, setSortCol, setSortDir);

  const selectedSiteName = sites.find(s => s.id === siteId)?.siteName;

  const filteredData = searchText.trim()
    ? data.filter(d => {
        const q = searchText.trim().toLowerCase();
        return (d.wellName ?? '').toLowerCase().includes(q) || (d.description ?? '').toLowerCase().includes(q);
      })
    : data;

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.floor(offset / pageSize) + 1;

  const colTemplate = 'minmax(150px,1.4fr) minmax(160px,1.6fr) 88px 84px 104px 104px 104px 84px 108px 150px';
  const gridMinWidth = 1140;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Top panels: Create Model + Process Model */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18 }}>
        {/* Create Model */}
        <Panel title="Create Model" icon={<Layers size={16} />}>
          <Field label="Company Name">
            {isSuperAdmin ? (
              <SelectInput value={companyId} onChange={(e) => setCompanyId(Number(e.target.value))}>
                <option value={0}>Please select</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.companyName}</option>)}
              </SelectInput>
            ) : (
              <TextInput value={ownCompanyName} readOnly style={{ background: 'var(--color-panel-alt)', color: 'var(--color-text-mid)', cursor: 'not-allowed' }} />
            )}
          </Field>
          <Field label="Surface Location">
            <SelectInput value={siteId} onChange={(e) => setSiteId(Number(e.target.value))} disabled={isSuperAdmin && companyId <= 0}>
              <option value={0}>Please select</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.siteName}</option>)}
            </SelectInput>
          </Field>
          <Field label="Well Name">
            <SelectInput value={wellId} onChange={(e) => setWellId(Number(e.target.value))} disabled={siteId <= 0}>
              <option value={0}>Please select</option>
              {wells.map((w) => <option key={w.id} value={w.id}>{w.wellName}</option>)}
            </SelectInput>
          </Field>
          <Field label="LAS File Name">
            <SelectInput value={lasFileId} onChange={(e) => setLasFileId(Number(e.target.value))} disabled={wellId <= 0}>
              <option value={0}>Please select</option>
              {lasFiles.map((f) => <option key={f.id} value={f.id}>{f.fileName}</option>)}
            </SelectInput>
          </Field>
        </Panel>

        {/* Process and View */}
        <Panel title="Process and View Models" icon={<Settings2 size={16} />}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="From Length (m)">
              <TextInput type="number" value={fromLength} onChange={(e) => setFromLength(e.target.value)} placeholder="e.g. 1000" />
            </Field>
            <Field label="To Length (m)">
              <TextInput type="number" value={toLength} onChange={(e) => setToLength(e.target.value)} placeholder="e.g. 1200" />
            </Field>
          </div>
          <Field label="Model Description">
            <TextInput value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Section A run 1" />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
            <PrimaryButton onClick={handleProcess} disabled={processing || !wellId || !lasFileId}>
              <RefreshCw size={15} className={processing ? 'animate-spin' : ''} />
              {processing ? 'Processing…' : 'Process Model'}
            </PrimaryButton>
          </div>
        </Panel>
      </div>

      {/* Results grid */}
      <div style={{ background: 'var(--color-panel)', border: '1px solid var(--color-border)', borderRadius: 10, width: 'fit-content', minWidth: '100%' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'var(--color-panel-alt)', border: '1px solid var(--color-border)', borderRadius: 8, width: 280 }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search well or description…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13.5, flex: 1, fontFamily: 'var(--font-body)', color: 'var(--color-text)', width: '100%' }}
            />
            {searchText && (
              <button onClick={() => setSearchText('')} aria-label="Clear search" title="Clear search"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 9, padding: 0, background: 'transparent', border: 'none', color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0 }}>
                <X size={13} />
              </button>
            )}
          </div>

          <GhostButton onClick={loadGrid}>
            <RefreshCw size={14} /> Refresh
          </GhostButton>

          <div style={{ flex: 1 }} />

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {selectedSiteName ? <>Surface Location: <span style={{ color: 'var(--color-text-mid)' }}>{selectedSiteName}</span> · </> : ''}
            {loading ? 'Loading…' : `${filteredData.length} model${filteredData.length === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Header + rows */}
        <div >
          <div
            className="grid-bordered"
            style={{
              display: 'grid',
              gridTemplateColumns: colTemplate,
              columnGap: 0,
              minWidth: gridMinWidth,
              padding: 0,
              alignItems: 'stretch',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
              position: 'sticky',
              top: 72,
              zIndex: 10,
              fontSize: 14,
              letterSpacing: 0.3,
              color: 'var(--color-text)',
              fontWeight: 600,
              fontFamily: 'var(--font-body)',
            }}
          >
            <SortHeader label="Well Name" col="WellName" activeCol={sortCol} dir={sortDir} onClick={handleSort} />
            <SortHeader label="Description" col="Description" activeCol={sortCol} dir={sortDir} onClick={handleSort} />
            <SortHeader label="From (m)" col="FromLength" activeCol={sortCol} dir={sortDir} onClick={handleSort} />
            <SortHeader label="To (m)" col="ToLength" activeCol={sortCol} dir={sortDir} onClick={handleSort} />
            <SortHeader label="Perfect Vol (m³)" col="PerfectVolume" activeCol={sortCol} dir={sortDir} onClick={handleSort} />
            <SortHeader label="Actual Vol (m³)" col="ActualVolume" activeCol={sortCol} dir={sortDir} onClick={handleSort} />
            <SortHeader label="Difference (m³)" col="DiffVolume" activeCol={sortCol} dir={sortDir} onClick={handleSort} />
            <SortHeader label="Time (s)" col="TimeTakenToProcessModel" activeCol={sortCol} dir={sortDir} onClick={handleSort} />
            <SortHeader label="Status" col="PlyFileStatus" activeCol={sortCol} dir={sortDir} onClick={handleSort} />
            <div style={{ textAlign: 'right' }}>Actions</div>
          </div>

          {loading ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>Loading…</div>
          ) : siteId <= 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
              <Box size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
              <div style={{ fontSize: 14, marginBottom: 4 }}>Select a location to view its 3D models</div>
              <div style={{ fontSize: 12 }}>Choose a Location Name above to load processed models.</div>
            </div>
          ) : filteredData.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
              <Box size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
              <div style={{ fontSize: 14, marginBottom: 4 }}>{searchText ? 'No models match your search' : 'No 3D models for this location'}</div>
              <div style={{ fontSize: 12 }}>{searchText ? 'Try a different search.' : 'Process a model above to get started.'}</div>
            </div>
          ) : (
            <div>
              {filteredData.map((row, i) => (
                <ModelRow
                  key={row.id ?? i}
                  row={row}
                  colTemplate={colTemplate}
                  gridMinWidth={gridMinWidth}
                  isLast={i === filteredData.length - 1}
                  onView={() => open3DViewer(row)}
                  onDownload={() => openDownloadModal(row)}
                  onDelete={() => handleDelete(row)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination footer */}
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--color-text-dim)', background: 'var(--color-panel-alt)', borderRadius: '0 0 10px 10px' }}>
          <div>
            Showing <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{filteredData.length}</span>{' '}
            of <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{totalCount}</span> models
          </div>
          <PaginationControls currentPage={currentPage} totalPages={totalPages} onPage={(p) => setOffset((p - 1) * pageSize)} />
        </div>
      </div>

      {/* Download modal */}
      <FormModal
        open={downloadModal}
        onClose={() => setDownloadModal(false)}
        title="3D Cloud Download"
        icon={<Download size={20} />}
        footer={<GhostButton onClick={() => setDownloadModal(false)}>Close</GhostButton>}
      >
        {selectedCloud && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <DownloadOption
              disabled={downloading}
              onClick={() => handleDownload('A')}
              label={`${selectedCloud.wellName}_${selectedCloud.fromLength}m-${selectedCloud.toLength}m_Actual_${selectedCloud.id}.ply`}
            />
            <DownloadOption
              disabled={downloading}
              onClick={() => handleDownload('P')}
              label={`${selectedCloud.wellName}_${selectedCloud.fromLength}m-${selectedCloud.toLength}m_Perfect_${selectedCloud.id}.ply`}
            />
          </div>
        )}
      </FormModal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────

function Panel({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <div style={{ background: 'var(--color-panel)', border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 16px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-panel-alt)' }}>
        <span style={{ display: 'flex', color: 'var(--color-accent)' }}>{icon}</span>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--color-text)', fontFamily: 'var(--font-body)' }}>{title}</span>
      </div>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
    </div>
  );
}

function StatusPill({ status }: { status: number | null }) {
  if (status === 2) {
    return (
      <span style={pillStyle('rgba(26,143,90,0.12)', 'rgba(26,143,90,0.30)', 'var(--color-good)')}>
        <Check size={12} /> Complete
      </span>
    );
  }
  if (status === 1) {
    return (
      <span style={pillStyle('rgba(183,121,31,0.14)', 'rgba(183,121,31,0.32)', 'var(--color-warn)')}>
        <Loader2 size={12} className="animate-spin" /> Processing
      </span>
    );
  }
  return (
    <span style={pillStyle('var(--color-panel-alt)', 'var(--color-border)', 'var(--color-text-dim)')}>
      <Clock size={12} /> Pending
    </span>
  );
}

function pillStyle(bg: string, border: string, color: string) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 12,
    background: bg, border: `1px solid ${border}`, color, fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-body)',
  } as const;
}

/** Colored row-action icon button (accent / good / danger variants). */
function ActionIcon({ title, onClick, variant, children }: { title: string; onClick: () => void; variant: 'accent' | 'good' | 'danger'; children: ReactNode }) {
  const [hover, setHover] = useState(false);
  // Solid at idle (client request), darker on hover.
  const palette = {
    accent: { solid: 'var(--color-accent)', hover: 'var(--color-accent-hover)' },
    good: { solid: 'var(--color-good)', hover: '#15794c' },
    danger: { solid: 'var(--color-bad)', hover: '#991b1b' },
  }[variant];
  return (
    <button
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8,
        border: `1px solid ${hover ? palette.hover : palette.solid}`,
        background: hover ? palette.hover : palette.solid,
        color: '#fff',
        cursor: 'pointer', transition: 'background 120ms, color 120ms, border-color 120ms, transform 120ms',
        transform: hover ? 'translateY(-1px)' : 'none',
        boxShadow: hover ? '0 2px 6px rgba(15,23,32,0.12)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

function ModelRow({
  row, colTemplate, gridMinWidth, isLast, onView, onDownload, onDelete,
}: {
  row: CloudProcessItem; colTemplate: string; gridMinWidth: number; isLast: boolean;
  onView: () => void; onDownload: () => void; onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const wrap = { overflowWrap: 'anywhere' as const, wordBreak: 'break-word' as const };
  const mono = { fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--color-text-mid)' };
  const num = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toFixed(2));

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="grid-bordered"
      style={{
        display: 'grid',
        gridTemplateColumns: colTemplate,
        columnGap: 0,
        minWidth: gridMinWidth,
        padding: 0,
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'stretch',
        fontSize: 14,
        background: hover ? 'var(--color-panel-alt)' : 'transparent',
        transition: 'background 120ms',
      }}
    >
      <Cell style={{ ...wrap, fontWeight: 500 }}>{row.wellName || '—'}</Cell>
      <Cell style={{ ...wrap, color: 'var(--color-text-mid)' }}>{row.description || '—'}</Cell>
      <Cell style={mono}>{row.fromLength ?? '—'}</Cell>
      <Cell style={mono}>{row.toLength ?? '—'}</Cell>
      <Cell style={mono}>{num(row.perfectVolume)}</Cell>
      <Cell style={mono}>{num(row.actualVolume)}</Cell>
      <Cell style={mono}>{num(row.diffVolume)}</Cell>
      <Cell style={mono}>{row.timeTakenToProcessModel ?? '—'}</Cell>
      <div><StatusPill status={row.plyFileStatus} /></div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <ActionIcon title="View 3D Model" onClick={onView} variant="accent"><ExternalLink size={16} /></ActionIcon>
        <ActionIcon title="Download PLY Files" onClick={onDownload} variant="good"><Download size={16} /></ActionIcon>
        <ActionIcon title="Delete" onClick={onDelete} variant="danger"><Trash2 size={16} /></ActionIcon>
      </div>
    </div>
  );
}

function DownloadOption({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', padding: '14px 16px',
        borderRadius: 10, border: '1px solid var(--color-border)',
        background: hover && !disabled ? 'var(--color-panel-alt)' : 'var(--color-panel)',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, transition: 'background 120ms',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 9, background: 'rgba(87,154,59,0.10)', color: 'var(--color-accent)', flexShrink: 0 }}>
        <Download size={18} />
      </span>
      <span style={{ fontSize: 13, color: 'var(--color-text)', fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{label}</span>
    </button>
  );
}
